import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatRupiah, priceInfo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, Loader2, ShoppingCart, Minus, Plus, RotateCcw, Clock, TriangleAlert, MessageCircle, FileText } from "lucide-react";
import ProductGallery from "@/components/ProductGallery";

export default function Customizer() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addItem } = useCart();
  const fileRef = useRef(null);

  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [selections, setSelections] = useState({});
  const [designs, setDesigns] = useState([]);
  const [qty, setQty] = useState(1);
  const [allProducts, setAllProducts] = useState([]);
  useEffect(() => { api.get("/products").then((r) => setAllProducts(r.data)).catch(() => {}); }, []);
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get(`/products/${productId}`).then((r) => {
      const p = r.data;
      setProduct(p);
      setQty(p.min_order || 1);
      const init = {};
      (p.variant_groups || []).forEach((g) => { if (g.options?.length) init[g.name] = 0; });
      const cbs = p.variant_combinations || [];
      if (cbs.length) {
        const inStock = cbs.find((c) => c.stock == null || c.stock > 0);
        if (inStock) (p.variant_groups || []).forEach((g, gi) => {
          const oi = g.options.findIndex((o) => o.label === inStock.labels[gi]);
          if (oi >= 0) init[g.name] = oi;
        });
      }
      setSelections(init);
    }).catch(() => { toast.error("Produk tidak ditemukan"); navigate("/produk"); });
  }, [productId, navigate]);

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const slots = 4 - designs.length;
    if (slots <= 0) { toast.error("Maksimal 4 file desain"); return; }
    const valid = [];
    for (const f of files.slice(0, slots)) {
      if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) { toast.error(`${f.name}: format tidak didukung`); continue; }
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name}: maksimal 8MB`); continue; }
      valid.push({ file: f, preview: URL.createObjectURL(f), note: "" });
    }
    setDesigns((d) => [...d, ...valid]);
    if (fileRef.current) fileRef.current.value = "";
  };
  const removeDesign = (i) => setDesigns((d) => d.filter((_, idx) => idx !== i));
  const setDesignNote = (i, note) => setDesigns((d) => d.map((x, idx) => idx === i ? { ...x, note } : x));

  if (!product) return <div className="App"><Navbar /><div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#F0005A]" /></div></div>;

  const isCustom = product.product_kind !== "ready";
  const groups = product.variant_groups || [];
  const combos = product.variant_combinations || [];
  const selLabels = groups.map((g) => g.options?.[selections[g.name] ?? 0]?.label);
  const activeCombo = combos.length ? combos.find((c) => (c.labels || []).join("\u241E") === selLabels.join("\u241E")) : null;

  const { base, promo, effective, discount } = priceInfo(product);
  let unitPrice = effective;
  let strikeBase = promo != null ? base : null;
  let showDiscount = discount;
  if (activeCombo) {
    const cprice = Number(activeCombo.price) || 0;
    const cpromo = Number(activeCombo.promo_price) || 0;
    const promoValid = cpromo > 0 && cpromo < cprice;
    unitPrice = promoValid ? cpromo : (cprice > 0 ? cprice : effective);
    strikeBase = promoValid ? cprice : null;
    showDiscount = promoValid ? Math.round((1 - cpromo / cprice) * 100) : 0;
  } else if (!combos.length) {
    const finalOverride = groups.reduce((val, g) => { const p = g.options?.[selections[g.name] ?? 0]?.price_delta || 0; return p > 0 ? p : val; }, 0);
    if (finalOverride > 0) { unitPrice = finalOverride; strikeBase = null; showDiscount = 0; }
  }

  const variantImage = activeCombo?.image_url || "";
  const comboImages = combos.map((c) => c.image_url).filter(Boolean);
  const galleryBase = (product.images && product.images.length) ? product.images : (product.image_url ? [product.image_url] : []);
  const gallery = Array.from(new Set([...galleryBase, ...comboImages]));

  const outOfStock = activeCombo ? (activeCombo.stock != null && activeCombo.stock <= 0) : (combos.length > 0);
  const optionUnavailable = (gi, label) => {
    if (!combos.length) return false;
    const rel = combos.filter((c) => (c.labels || [])[gi] === label);
    return rel.length > 0 && rel.every((c) => c.stock != null && c.stock <= 0);
  };

  const minOrder = product.min_order || 1;
  const belowMin = qty < minOrder;
  const recs = (() => {
    const others = allProducts.filter((x) => x.id !== product.id);
    const same = others.filter((x) => x.type === product.type);
    return (same.length >= 4 ? same : others).slice(0, 4);
  })();

  const buildSelections = () => groups.map((g) => {
    const opt = g.options?.[selections[g.name] ?? 0];
    return opt ? { group_name: g.name, option_label: opt.label } : null;
  }).filter(Boolean);

  const submit = async (mode) => {
    if (belowMin) { toast.error(`Minimal pemesanan untuk produk ini adalah ${minOrder} pcs`); return; }
    if (outOfStock) { toast.error("Varian yang dipilih sedang habis"); return; }
    let design_files = [];
    if (isCustom && designs.length) {
      if (!user) { sessionStorage.setItem("post_login_redirect", `/kustom/${productId}`); toast.info("Silakan masuk untuk mengunggah desain"); navigate("/login"); return; }
      setUploading(true);
      try {
        for (const d of designs) {
          const fd = new FormData(); fd.append("file", d.file);
          const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          design_files.push({ file_id: res.data.file_id, note: d.note || "" });
        }
      } catch (e) { toast.error("Gagal mengunggah desain"); setUploading(false); return; }
      setUploading(false);
    }
    const first = design_files[0];
    addItem({
      product_id: product.id, product_name: product.name, product_type: product.type,
      product_kind: product.product_kind, image_url: variantImage || gallery[0] || "", unit_price: unitPrice, weight: activeCombo?.weight ?? product.weight ?? 500,
      quantity: qty, variant_selections: buildSelections(),
      design_files, design_file_id: first?.file_id || null,
      design_url: first ? `${api.defaults.baseURL}/files/${first.file_id}` : null,
      min_order: minOrder, notes,
    });
    if (mode === "buy") { navigate("/checkout"); }
    else { toast.success("Ditambahkan ke keranjang"); }
  };

  return (
    <div className="App">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid lg:grid-cols-2 gap-10">
          {/* Gallery */}
          <ProductGallery images={gallery} video={product.video_url} activeSrc={variantImage} overlay={<>
            {showDiscount > 0 && <span className="absolute top-4 left-4 z-20 bg-[#F0005A] text-white text-xs font-bold px-2.5 py-1 rounded-full">Diskon {showDiscount}%</span>}
            {product.preorder && <span className="absolute top-4 right-4 z-20 bg-[#00133F] text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1"><Clock className="w-3 h-3" /> PRE-ORDER</span>}
          </>} />

          {/* Config */}
          <div>
            <p className="text-sm text-slate-500 uppercase tracking-wider mb-1">{isCustom ? "Produk Custom" : "Ready Stok"}</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#00133F]">{product.name}</h1>

            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <span className="text-2xl font-bold text-[#F0005A]" data-testid="customizer-price">{formatRupiah(unitPrice)}</span>
              {strikeBase != null && <span className="text-lg text-slate-400 line-through" data-testid="strikethrough-price">{formatRupiah(strikeBase)}</span>}
              {showDiscount > 0 && <span className="text-xs font-bold text-[#F0005A] bg-[#F0005A]/10 px-2 py-1 rounded-full">Diskon {showDiscount}%</span>}
              <span className="text-sm text-slate-400">/ pcs</span>
            </div>

            {product.preorder && (
              <div className="mt-4 rounded-xl bg-[#00133F]/5 border border-[#00133F]/15 p-3 flex items-center gap-2" data-testid="preorder-info">
                <Clock className="w-4 h-4 text-[#00133F]" />
                <p className="text-sm text-[#00133F]">Pre-Order · estimasi proses <strong>{product.preorder_days || "beberapa"} hari kerja</strong> sebelum dikirim.</p>
              </div>
            )}

            <p className="text-sm text-slate-500 mt-3" data-testid="min-order-info">Min. Pemesanan: <strong>{minOrder} pcs</strong></p>

            {/* Variant groups */}
            {groups.map((g, gi) => (
              <div key={g.name} className="mt-6">
                <Label className="text-sm font-semibold text-[#00133F]">{g.name}</Label>
                <div className="flex flex-wrap gap-2 mt-2" data-testid={`variant-group-${g.name}`}>
                  {g.options.map((opt, i) => {
                    const active = (selections[g.name] ?? 0) === i;
                    const soldOut = optionUnavailable(gi, opt.label);
                    return (
                      <button key={i} onClick={() => !soldOut && setSelections((s) => ({ ...s, [g.name]: i }))} disabled={soldOut}
                        data-testid={`variant-${g.name}-${i}`}
                        className={`px-4 py-2 rounded-full border text-sm transition-colors ${soldOut ? "border-slate-200 text-slate-300 line-through cursor-not-allowed bg-slate-50" : active ? "border-[#F0005A] bg-[#F0005A]/10 text-[#F0005A] font-medium" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                        {opt.label}{soldOut ? " · Habis" : ""}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Optional multi-upload (custom only) */}
            {isCustom && (
              <div className="mt-6">
                <Label className="text-sm font-semibold text-[#00133F]">Unggah Desain (Opsional) — maks. 4 file</Label>
                <input ref={fileRef} type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onPick} data-testid="design-file-input" />
                {designs.length > 0 && (
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {designs.map((d, i) => (
                      <div key={i} className="rounded-xl border border-slate-200 p-2" data-testid={`design-item-${i}`}>
                        <div className="flex gap-2">
                          <img src={d.preview} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                          <button onClick={() => removeDesign(i)} className="ml-auto text-slate-400 hover:text-red-500 h-fit"><RotateCcw className="w-4 h-4" /></button>
                        </div>
                        <input value={d.note} onChange={(e) => setDesignNote(i, e.target.value)} placeholder="Keterangan (mis. Sisi Depan)"
                          className="mt-2 w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#F0005A]/50" data-testid={`design-note-${i}`} />
                      </div>
                    ))}
                  </div>
                )}
                {designs.length < 4 && (
                  <button data-testid="upload-image-btn" onClick={() => fileRef.current?.click()}
                    className="mt-2 w-full border-2 border-dashed border-slate-300 rounded-2xl p-5 flex flex-col items-center gap-2 hover:border-[#F0005A] hover:bg-[#F0005A]/5 transition-colors">
                    <Upload className="w-6 h-6 text-[#00133F]" />
                    <p className="text-sm font-medium text-[#00133F]">Tambah desain (opsional)</p>
                    <p className="text-xs text-slate-500">PNG, JPG, WEBP · maks 8MB · sampai 4 file</p>
                  </button>
                )}
                <div className="mt-2 rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
                  <TriangleAlert className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700 leading-relaxed">Pastikan gambar beresolusi tinggi (min. 300 DPI). Kami tidak bertanggung jawab atas hasil cetak buram karena kualitas file asli.</p>
                </div>
                <div className="mt-2 rounded-xl bg-slate-50 border border-slate-200 p-3 flex items-start gap-2" data-testid="large-file-instruction">
                  <FileText className="w-4 h-4 text-[#00133F] shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-600 leading-relaxed">Untuk file desain berukuran besar (di atas 10MB) seperti PDF, PSD, AI, atau CDR, mohon kirimkan ke email kami: <a href="mailto:rooftopprint@gmail.com" className="text-[#F0005A] font-medium">rooftopprint@gmail.com</a> dengan mencantumkan Nomor Pesanan di subjek email.</p>
                </div>
                {designs.length === 0 && (
                  <div className="mt-2 flex items-start gap-2 text-xs text-slate-500" data-testid="no-design-note">
                    <MessageCircle className="w-4 h-4 shrink-0 mt-0.5 text-[#25D366]" />
                    <span>Tidak unggah sekarang? Tidak masalah — kirim desain Anda via Chat/WA setelah pesanan dibuat.</span>
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            <div className="mt-6">
              <Label className="text-sm font-semibold text-[#00133F]">Catatan untuk tim (opsional)</Label>
              <Textarea data-testid="notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Contoh: warna dominan biru, tulisan 'Selamat Wisuda'" className="mt-2 rounded-xl focus-visible:ring-[#F0005A]/50" />
            </div>

            {/* Qty + actions (Shopee-style) */}
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <div className="flex items-center border border-slate-200 rounded-full overflow-hidden">
                <button className="px-4 py-3 hover:bg-slate-50" onClick={() => setQty((q) => Math.max(1, (Number(q) || 1) - 1))} data-testid="qty-minus"><Minus className="w-4 h-4" /></button>
                <input type="number" min={1} value={qty} data-testid="qty-value"
                  onChange={(e) => { const v = parseInt(e.target.value, 10); setQty(Number.isNaN(v) ? "" : Math.max(1, v)); }}
                  onBlur={(e) => { if (!e.target.value || Number(e.target.value) < 1) setQty(1); }}
                  className="w-16 text-center font-semibold outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                <button className="px-4 py-3 hover:bg-slate-50" onClick={() => setQty((q) => (Number(q) || 0) + 1)} data-testid="qty-plus"><Plus className="w-4 h-4" /></button>
              </div>
              <span className="text-sm text-slate-500">Subtotal: <strong className="text-[#00133F]">{formatRupiah(unitPrice * qty)}</strong></span>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <Button data-testid="add-to-cart-btn" onClick={() => submit("cart")} disabled={uploading || belowMin || outOfStock} variant="outline"
                className="flex-1 h-12 rounded-full border-2 border-[#F0005A] text-[#F0005A] hover:bg-[#F0005A]/5 active:scale-95 transition-transform text-base disabled:opacity-50">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Mengunggah...</> : outOfStock ? "Stok Habis" : <><ShoppingCart className="w-4 h-4 mr-2" /> Masukkan Keranjang</>}
              </Button>
              <Button data-testid="buy-now-btn" onClick={() => submit("buy")} disabled={uploading || belowMin || outOfStock}
                className="flex-1 h-12 rounded-full bg-[#F0005A] hover:brightness-110 active:scale-95 transition-transform text-white text-base disabled:opacity-50">
                {outOfStock ? "Stok Habis" : "Beli Sekarang"}
              </Button>
            </div>
            {outOfStock && <p className="mt-3 text-xs text-red-500" data-testid="out-of-stock-warning">Varian yang dipilih sedang habis. Silakan pilih varian lain.</p>}
            {belowMin && <p className="mt-3 text-xs text-red-500" data-testid="min-order-warning">Maaf, minimal pemesanan untuk produk ini adalah {minOrder} pcs.</p>}
          </div>
        </div>

        {/* Description (moved to bottom) */}
        <div className="mt-10 pt-8 border-t border-slate-100" data-testid="product-description">
          <h2 className="font-heading text-xl font-semibold text-[#00133F] mb-3">Deskripsi Produk</h2>
          <p className="text-slate-600 leading-relaxed whitespace-pre-line max-w-3xl">{product.description}</p>
        </div>
      </div>
      {recs.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="recommendations">
          <h2 className="font-heading text-2xl font-bold text-[#00133F] mb-6">Mungkin Anda Juga Suka</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {recs.map((r) => (
              <Link key={r.id} to={`/kustom/${r.id}`} data-testid={`rec-product-${r.id}`}
                className="group rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-lg hover:-translate-y-1 transition-all">
                <div className="aspect-square bg-slate-50 overflow-hidden">
                  <img src={r.image_url} alt={r.name} loading="lazy" className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform" />
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-[#00133F] text-sm truncate">{r.name}</h3>
                  <p className="text-[#F0005A] font-bold mt-1">{priceInfo(r).from ? "mulai " : ""}{formatRupiah(priceInfo(r).effective)}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}
